import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Stepper } from "./Stepper";
import { SectionNav } from "./SectionNav";
import { TrustBar } from "./TrustBar";
import { SidebarInfo } from "./SidebarInfo";

/**
 * SignUpShell — shared chrome for the patient + caregiver registration flows.
 *
 * Modes:
 *   - mode="single-page" (default): renders a sticky SectionNav at the top of
 *     the form card. Form content is rendered as one long scroll. Pass
 *     `sections` + `completedIds` for scroll-spy highlighting.
 *   - mode="multi-step": renders a numbered Stepper. Pass `steps` + `currentStep`.
 */
export const SignUpShell = ({
  audience = "patient",
  eyebrow = "Patient sign-up",
  heroTitle,
  heroSubtitle,
  timeEstimate = "Takes about 3 minutes",
  backTo = "/patient",
  backLabel = "Back to Patient Hub",

  // Single-page mode
  mode = "single-page",
  sections = [],
  completedIds = new Set(),

  // Multi-step mode (legacy)
  steps = [],
  currentStep = 1,

  children,
  mobileCta,
}) => {
  return (
    <div className="bg-white min-h-screen pb-32 md:pb-16">
      {/* Hero — slimmer for single-page mode */}
      <section className="bg-[var(--dark-bg)] grain-dark relative overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-32 -right-32 h-[420px] w-[420px] rounded-full bg-[var(--lime)]/10 blur-3xl"
        />
        <div className="container-main pt-8 md:pt-12 pb-20 md:pb-24 relative z-10">
          <Link
            to={backTo}
            className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white transition-colors mb-5"
            data-testid="signup-back-link"
          >
            <ArrowLeft size={14} />
            {backLabel}
          </Link>
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-[var(--lime)] mb-4">
            <span className="w-6 h-px bg-[var(--lime)]" />
            {eyebrow}
          </span>
          <h1 className="font-['Outfit'] font-bold text-white leading-[1.05] tracking-tight text-3xl md:text-4xl lg:text-[44px] mb-3 max-w-2xl">
            {heroTitle}
          </h1>
          {heroSubtitle && (
            <p className="text-base md:text-lg text-white/80 leading-relaxed max-w-2xl">
              {heroSubtitle}
            </p>
          )}
          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-3">
            {timeEstimate && (
              <p className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-[var(--lime)]">
                <span className="h-1.5 w-1.5 rounded-full bg-[var(--lime)] animate-pulse" />
                {timeEstimate}
              </p>
            )}
            <TrustBar variant="dark" />
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="container-main -mt-12 md:-mt-14 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          {/* FORM column */}
          <div className="lg:col-span-8">
            <div className="bg-[#f1f5f9] rounded-2xl overflow-hidden">
              {/* Sticky section nav (or stepper) */}
              <div className="sticky top-20 z-20 bg-[#f1f5f9]/95 backdrop-blur-md border-b border-[var(--light-border)] px-4 md:px-8 py-3">
                {mode === "single-page" ? (
                  <SectionNav
                    sections={sections}
                    completedIds={completedIds}
                    offset={132}
                  />
                ) : (
                  <Stepper steps={steps} current={currentStep} />
                )}
              </div>
              <div className="px-6 md:px-10 py-8 md:py-10">{children}</div>
            </div>
          </div>

          {/* SIDEBAR column (hidden on small screens) */}
          <div className="hidden lg:block lg:col-span-4">
            <SidebarInfo audience={audience} />
          </div>
        </div>
      </section>

      {/* Sticky mobile CTA */}
      {mobileCta && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-[var(--light-border)] p-3 px-4 shadow-[0_-12px_30px_-12px_rgba(15,23,42,0.15)]">
          {mobileCta}
        </div>
      )}

      <style>{`
        @keyframes stepIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default SignUpShell;
